import { useMemo, useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

import useAuthStore from '@entities/auth/model/useAuthStore';
import useUnsavedLeaveStore, { UNSAVED_SCOPE } from '@shared/model/useUnsavedLeaveStore';
import {
  useDocFormListAdminQuery,
  useUpdateFormMutation,
  useUpdateFormsBatchMutation,
} from '@features/form-selection/hooks';
import AdminLayout from '@shared/components/organisms/AdminLayout/AdminLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import SearchFilter from '@shared/components/organisms/SearchFilter/SearchFilter';
import ApprovalDataTable from '@shared/components/organisms/ApprovalDataTable/ApprovalDataTable';
import Pagination from '@shared/components/organisms/Pagination/Pagination';
import Button from '@shared/components/atoms/Button/Button';
import Alert from '@shared/components/atoms/Alert';
import Confirm from '@shared/components/atoms/Confirm/Confirm';
import { useAlert, useConfirm } from '@shared/hooks';
import useSort from '@shared/hooks/useSort';
import dragIcon from '@shared/assets/images/drag_icon.svg';

import styles from './DocumentFormsManagementPage.module.scss';

/**
 * DocumentFormsManagementPage
 * - 관리자 문서 양식 관리 페이지
 */
export default function DocumentFormsManagementPage() {
  const navigate = useNavigate();
  const companyCode = useAuthStore((state) => state.companyCode);

  // Alert / Confirm 상태
  const { alertState, showAlert, closeAlert } = useAlert();
  const { confirmState, showConfirm, closeConfirm } = useConfirm();

  // LNB/로그아웃 시 저장 없이 이동 확인용 store (pendingLeave 시 전역 Confirm 표시)
  const pendingLeave = useUnsavedLeaveStore((state) => state.pendingLeave);
  const confirmLeave = useUnsavedLeaveStore((state) => state.confirmLeave);
  const cancelLeave = useUnsavedLeaveStore((state) => state.cancelLeave);
  const setUnsaved = useUnsavedLeaveStore((state) => state.setUnsaved);
  const clearUnsaved = useUnsavedLeaveStore((state) => state.clearUnsaved);

  // 필터 상태
  const [filterValues, setFilterValues] = useState({
    documentCategory: '',
    useYn: '',
    formName: '',
  });

  // 목록 편집 모드 상태
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableData, setEditableData] = useState([]);
  const [initialData, setInitialData] = useState([]);

  // 드래그 앤 드롭 상태
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null);

  // 어드민 양식 목록 조회 (페이지 진입 시 사용자 회사코드로만 조회)
  const {
    data: docFormList = [],
    isLoading,
    error,
    refetch: refetchDocFormList,
  } = useDocFormListAdminQuery(companyCode);

  // 추가/수정/삭제 후 목록 복귀 시 캐시만 무효화되어 있으므로, 목록 페이지 진입 시 한 번 refetch하여 최신 목록 표시
  // 의도: 마운트 시에만 실행 (의존성에 refetch를 넣으면 라이브러리 구현에 따라 불필요한 재실행 가능성 있음)
  useEffect(function () {
    refetchDocFormList();
  }, []);

  // Mutation 훅
  const updateFormMutation = useUpdateFormMutation();
  const updateFormsBatchMutation = useUpdateFormsBatchMutation();

  // 문서분류 옵션 생성 (조회된 양식 목록 내의 카테고리만 표시)
  const categoryOptions = useMemo(
    () => ['전체', ...new Set(docFormList.map((item) => item.category).filter(Boolean))],
    [docFormList],
  );

  // 필터 필드 정의 (1열: 문서분류|사용여부, 2열: 양식명)
  const filterFields = useMemo(
    () => [
      {
        key: 'documentCategory',
        type: 'select',
        label: '문서분류',
        value: filterValues.documentCategory || '',
        options: categoryOptions,
      },
      {
        key: 'useYn',
        type: 'select',
        label: '사용여부',
        value: filterValues.useYn || '',
        options: [
          { label: '전체', value: '' },
          { label: '사용', value: '1' },
          { label: '미사용', value: '0' },
        ],
        fill: true,
      },
      { newRow: true },
      {
        key: 'formName',
        type: 'input',
        label: '양식명',
        placeholder: '',
        value: filterValues.formName || '',
        fill: true,
        wide: true,
      },
    ],
    [filterValues, categoryOptions],
  );

  // 필터링된 데이터 (문서분류, 사용여부, 양식명은 프론트엔드에서 실시간 필터링)
  const filteredData = useMemo(() => {
    let filtered = docFormList;

    // 문서분류 필터
    if (filterValues.documentCategory && filterValues.documentCategory !== '전체') {
      filtered = filtered.filter((item) => item.category === filterValues.documentCategory);
    }

    // 사용여부 필터
    if (filterValues.useYn !== '') {
      filtered = filtered.filter((item) => String(item.useYn) === filterValues.useYn);
    }

    // 양식명 필터
    if (filterValues.formName) {
      filtered = filtered.filter((item) =>
        item.title.toLowerCase().includes(filterValues.formName.toLowerCase()),
      );
    }

    // sort 값으로 정렬
    return [...filtered].sort((a, b) => (a.sort || 0) - (b.sort || 0));
  }, [docFormList, filterValues.documentCategory, filterValues.useYn, filterValues.formName]);

  // 편집 모드 진입 시 초기 데이터 백업 및 편집 가능 데이터 설정
  // filteredData를 useMemo로 메모이제이션하여 의존성 배열 문제 해결
  const memoizedFilteredData = useMemo(
    function () {
      return filteredData;
    },
    [filteredData],
  );

  useEffect(
    function () {
      if (isEditMode && memoizedFilteredData.length > 0) {
        // 초기 데이터 백업 (깊은 복사)
        const backup = memoizedFilteredData.map((item) => ({
          ...item,
          sort: item.sort || 0,
          useYn: item.useYn || 0,
        }));
        setInitialData(backup);
        setEditableData(backup);
      } else if (!isEditMode) {
        // 편집 모드 종료 시 초기화
        setEditableData([]);
        setInitialData([]);
        setDraggedIndex(null);
        setDragOverIndex(null);
        setDragOverPosition(null);
      }
    },
    [isEditMode, memoizedFilteredData],
  );

  // 일반 모드: 헤더 정렬 적용. 기본은 sort 값 오름차순(DB 노출 순서)
  // 참고: filteredData가 매우 클 경우 정렬 비용이 늘어날 수 있음
  const { sortedData, sortField, sortDirection, handleSort } = useSort(filteredData, {
    field: 'sort',
    direction: 'asc',
  });

  // 테이블에 표시할 데이터 (편집 모드면 editableData, 일반 모드면 정렬된 filteredData)
  const displayData = isEditMode ? editableData : sortedData;

  // 테이블 컬럼 정의 (사용여부 | 문서 분류 | 양식명 | 최종수정일)
  const tableColumns = useMemo(
    function () {
      const baseColumns = [
        {
          key: 'useYn',
          label: '사용여부',
          width: '120px',
          sortable: true,
          render: function (value, item, index) {
            if (isEditMode) {
              // 편집 모드: 드래그 아이콘 + 드롭다운
              return (
                <div className={styles.editCell}>
                  <img src={dragIcon} alt="드래그" className={styles.dragIcon} />
                  <select
                    className={styles.useYnSelect}
                    value={item.useYn || 0}
                    onChange={function (e) {
                      handleUseYnChange(index, Number(e.target.value));
                    }}
                    onClick={function (e) {
                      e.stopPropagation();
                    }}
                  >
                    <option value={1}>사용</option>
                    <option value={0}>미사용</option>
                  </select>
                </div>
              );
            }
            // 일반 모드: 텍스트만 표시
            return value === 1 ? '사용' : '미사용';
          },
          stopPropagation: true,
        },
        {
          key: 'category',
          label: '문서 분류',
          width: '150px',
          sortable: true,
        },
        {
          key: 'title',
          label: '양식명',
          width: '540px',
          sortable: true,
        },
        {
          key: 'lastModifyDate',
          label: '최종수정일',
          width: '180px',
          sortable: true,
        },
      ];

      return baseColumns;
    },
    [isEditMode],
  );

  // 사용여부 변경 핸들러
  function handleUseYnChange(index, newValue) {
    setEditableData(function (prev) {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        useYn: newValue,
      };
      return updated;
    });
  }

  // 드래그 시작
  function handleDragStart(e, index) {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  }

  // 드래그 종료
  function handleDragEnd(e) {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 드래그 오버 (드롭 가능 영역 표시)
  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  // 카드 위에서 드래그 오버 (위/아래 구분)
  function handleCardDragOver(e, index) {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null) {
      return;
    }

    if (draggedIndex === index) {
      setDragOverIndex(null);
      setDragOverPosition(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const cardMiddle = rect.top + rect.height / 2;

    const position = mouseY < cardMiddle ? 'top' : 'bottom';

    setDragOverIndex(index);
    setDragOverPosition(position);
  }

  // 카드에서 드래그 리브
  function handleCardDragLeave(e) {
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 드롭 (순서 변경 및 sort 값 재계산)
  function handleDrop(e, dropIndex) {
    e.preventDefault();
    if (draggedIndex === null) {
      return;
    }

    if (draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setDragOverPosition(null);
      return;
    }

    const updated = [...editableData];
    const draggedItem = updated[draggedIndex];

    // dragOverPosition에 따라 삽입 위치 결정 (제거 전에 계산)
    let insertIndex = dropIndex;
    if (dragOverPosition === 'bottom') {
      insertIndex = dropIndex + 1;
    }

    // 드래그된 아이템 제거
    updated.splice(draggedIndex, 1);

    // 드래그된 아이템이 제거되면서 insertIndex 조정
    // draggedIndex보다 큰 인덱스는 모두 1씩 감소
    if (draggedIndex < insertIndex) {
      insertIndex = insertIndex - 1;
    }

    // 드롭 위치에 삽입
    updated.splice(insertIndex, 0, draggedItem);

    // sort 값 재계산 (새 객체 생성하여 React가 변경을 감지하도록)
    const updatedWithSort = updated.map(function (item, idx) {
      return {
        ...item,
        sort: idx + 1,
      };
    });

    setEditableData(updatedWithSort);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 변경된 항목 감지
  function getChangedItems() {
    const changed = [];

    editableData.forEach(function (currentItem) {
      const initialItem = initialData.find(function (item) {
        return item.doc === currentItem.doc;
      });

      if (!initialItem) {
        return;
      }

      // sort는 숫자로 비교 (타입 불일치 방지)
      const currentSort = Number(currentItem.sort) || 0;
      const initialSort = Number(initialItem.sort) || 0;

      // sort 또는 useYn이 변경된 경우
      if (currentSort !== initialSort || currentItem.useYn !== initialItem.useYn) {
        changed.push({
          initial: initialItem,
          current: currentItem,
        });
      }
    });

    return changed;
  }

  // 변경 여부 (저장 없이 이동 시 확인용)
  const hasChanges = useMemo(
    function () {
      return getChangedItems().length > 0;
    },
    [editableData, initialData],
  );

  // 저장 없이 이동 시 확인: dirty일 때만 store 등록, 해제 시 clearUnsaved
  useEffect(
    function () {
      if (isEditMode && hasChanges) {
        setUnsaved(UNSAVED_SCOPE.DOCUMENT_FORMS, function getResetAndProceed() {
          return function runResetThenNext(next) {
            setIsEditMode(false);
            setEditableData([]);
            setInitialData([]);
            clearUnsaved();
            if (typeof next === 'function') next();
          };
        });
      } else {
        clearUnsaved();
      }
      return function cleanup() {
        clearUnsaved();
      };
    },
    [isEditMode, hasChanges, setUnsaved, clearUnsaved],
  );

  // 저장 핸들러
  async function handleSave() {
    const changedItems = getChangedItems();

    if (changedItems.length === 0) {
      showAlert('변경된 내용이 없습니다.');
      return;
    }

    try {
      console.log('변경된 항목 수:', changedItems.length);

      // 전체 목록을 서버에 보낼 데이터 형식으로 변환 (배치 방식)
      const batchData = editableData.map((item) => {
        // initialData에서 해당 항목 찾기 (검토자 정보 포함)
        const initialItem = initialData.find((init) => init.doc === item.doc);

        // 검토자 정보 처리: 0이면 null로 보내어 기존 값 유지, 0이 아니면 실제 값 전송
        function getCheckValue(checkValue) {
          if (!checkValue || checkValue === 0 || checkValue === -1) {
            return null; // null을 보내면 SQL에서 업데이트하지 않음 (기존 값 유지)
          }
          return checkValue;
        }

        return {
          company: companyCode,
          doc: item.doc,
          useYn: item.useYn,
          sort: Number(item.sort) || 0,
          // 나머지 필드는 null로 보내어 기존 값을 유지함
          title: null,
          form: null,
          form_data: null,
          required_docs: null,
          category: null,
          senderList: null,
          executorList: null,
          // 검토자 정보는 initialData에서 가져와서 유지
          // null을 보내면 SQL에서 업데이트하지 않음 (기존 값 유지)
          check1: getCheckValue(initialItem?.check1),
          check2: getCheckValue(initialItem?.check2),
          check3: getCheckValue(initialItem?.check3),
          check4: getCheckValue(initialItem?.check4),
          // 결재라인 정보는 null로 보내어 기존 값 유지
          approvalLineEnabled: null,
          approvalLineList: null,
        };
      });

      console.log('API 호출: 양식 목록 배치 업데이트', batchData);

      await updateFormsBatchMutation.mutateAsync(batchData);

      console.log('배치 업데이트 완료');

      // 저장 완료 후 최신 데이터 재조회
      await refetchDocFormList();

      showAlert('저장되었습니다.', function () {
        clearUnsaved();
        setIsEditMode(false);
      });
    } catch (error) {
      console.error('저장 중 오류 발생:', error);
      const errorMessage =
        error?.response?.data?.error_msg ||
        error?.message ||
        error?.response?.data?.message ||
        '저장 중 오류가 발생했습니다.';
      showAlert(errorMessage);
    }
  }

  // 취소 핸들러 (변경사항 있으면 확인 후 편집 모드 해제)
  function handleCancel() {
    if (hasChanges) {
      showConfirm('작성 중인 내용이 있습니다. 저장하지 않고 이동하시겠습니까?', function () {
        clearUnsaved();
        setIsEditMode(false);
        setEditableData([]);
        setInitialData([]);
        closeConfirm();
      });
    } else {
      setIsEditMode(false);
    }
  }

  // 목록 편집 모드 진입
  function handleEditMode() {
    setIsEditMode(true);
  }

  // 필터 변경 핸들러
  function handleFilterChange(key, value) {
    setFilterValues(function (prev) {
      return {
        ...prev,
        [key]: value,
      };
    });
  }

  // 검색 핸들러 (필터만 적용, API 재요청 없음)
  function handleSearch() {
    // 필터링은 useMemo에서 자동으로 처리됨
  }

  // 필터 초기화 핸들러
  function handleReset() {
    // 1. 필터 UI 상태 초기화
    setFilterValues({
      documentCategory: '',
      useYn: '',
      formName: '',
    });

    // 2. 편집 모드 종료 (편집 중이던 내용 초기화)
    if (isEditMode) {
      setIsEditMode(false);
    }
  }

  // 행 클릭 핸들러 (사용/미사용 모두 상세(편집) 페이지 진입 가능)
  function handleRowClick(item) {
    // 편집 모드가 아닐 때만 편집 페이지로 이동
    if (!isEditMode) {
      navigate(`/admin/forms/document/edit/${item.doc}`, {
        state: {
          formInfo: {
            doc: item.doc,
            title: item.title || '',
            form: item.form || '',
            category: item.category || '',
            requiredDocs: item.requiredDocs || '',
            formDataHtml: item.formDataHtml || '',
            checkEmp: item.checkEmp || 0,
            checkName: item.checkName || '',
          },
        },
      });
    }
  }

  // 페이지 변경 핸들러
  function handlePageChange() {
    // TODO: 페이지 변경 처리
  }

  // 양식 추가 핸들러
  function handleAddForm() {
    navigate('/admin/forms/document/add');
  }

  const currentPage = 1;
  const totalPages = 1;

  return (
    <AdminLayout>
      <TabContentLayout
        tabs={{
          label: '문서 양식 관리',
          content: (
            <ContentCard className={styles.contentCardMarginFix}>
              {/* 검색/필터 섹션 */}
              <div className={styles.filterWrapper}>
                <SearchFilter
                  fields={filterFields}
                  onFilterChange={handleFilterChange}
                  onSearch={handleSearch}
                  onReset={handleReset}
                  showExcelButton={false}
                />
              </div>
              {/* 버튼 영역 */}
              <div className={styles.buttonWrap}>
                {isEditMode ? (
                  <>
                    <Button type="button" className={styles.actionButton} onClick={handleCancel}>
                      취소
                    </Button>
                    <Button
                      type="button"
                      className={styles.actionButton}
                      onClick={handleSave}
                      disabled={updateFormMutation.isPending || updateFormsBatchMutation.isPending}
                    >
                      저장
                    </Button>
                    <Button type="button" className={styles.actionButton} onClick={handleAddForm}>
                      + 양식 추가
                    </Button>
                  </>
                ) : (
                  <>
                    <Button type="button" className={styles.actionButton} onClick={handleEditMode}>
                      목록 편집
                    </Button>
                    <Button type="button" className={styles.actionButton} onClick={handleAddForm}>
                      + 양식 추가
                    </Button>
                  </>
                )}
              </div>
              {/* 데이터 테이블 */}
              <div className={styles.tableWrapper}>
                {isLoading ? (
                  <div className={styles.emptyCell}>로딩 중...</div>
                ) : error ? (
                  <div className={styles.emptyCell}>데이터를 불러오는 중 오류가 발생했습니다.</div>
                ) : (
                  <>
                    {isEditMode ? (
                      <div className={styles.tableContainer}>
                        <table className={styles.table}>
                          <thead>
                            <tr>
                              {tableColumns.map(function (column) {
                                return (
                                  <th
                                    key={column.key}
                                    className={column.className || ''}
                                    style={column.width ? { width: column.width } : undefined}
                                  >
                                    {column.label}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {displayData.map(function (item, index) {
                              const isDragged = draggedIndex === index;
                              const isDragOver = dragOverIndex === index;
                              const showTopLine = isDragOver && dragOverPosition === 'top';
                              const showBottomLine = isDragOver && dragOverPosition === 'bottom';

                              return (
                                <Fragment key={item.doc || index}>
                                  {/* 위쪽 드롭 인디케이터 */}
                                  {showTopLine && (
                                    <tr>
                                      <td
                                        colSpan={tableColumns.length}
                                        className={styles.dropIndicator}
                                      >
                                        <div className={styles.dropLine} />
                                      </td>
                                    </tr>
                                  )}
                                  {/* 데이터 행 */}
                                  <tr
                                    className={`${isDragged ? styles.dragging : ''} ${
                                      item.useYn === 0 ? styles.disabledRow : ''
                                    }`}
                                    draggable={isEditMode}
                                    onDragStart={function (e) {
                                      handleDragStart(e, index);
                                    }}
                                    onDragOver={function (e) {
                                      handleCardDragOver(e, index);
                                    }}
                                    onDragLeave={handleCardDragLeave}
                                    onDragEnd={handleDragEnd}
                                    onDrop={function (e) {
                                      handleDrop(e, index);
                                    }}
                                  >
                                    {tableColumns.map(function (column) {
                                      const cellValue = column.render
                                        ? column.render(item[column.key], item, index)
                                        : item[column.key];

                                      return (
                                        <td
                                          key={column.key}
                                          className={column.className || ''}
                                          style={column.width ? { width: column.width } : undefined}
                                          onClick={
                                            column.stopPropagation
                                              ? function (e) {
                                                  e.stopPropagation();
                                                }
                                              : undefined
                                          }
                                        >
                                          {cellValue}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                  {/* 아래쪽 드롭 인디케이터 */}
                                  {showBottomLine && (
                                    <tr>
                                      <td
                                        colSpan={tableColumns.length}
                                        className={styles.dropIndicator}
                                      >
                                        <div className={styles.dropLine} />
                                      </td>
                                    </tr>
                                  )}
                                </Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <ApprovalDataTable
                        columns={tableColumns}
                        data={displayData}
                        onRowClick={handleRowClick}
                        onSort={handleSort}
                        sortField={{ field: sortField, direction: sortDirection }}
                        emptyContent={<div className={styles.emptyCell}>양식이 없습니다.</div>}
                      />
                    )}
                    {/* {displayData.length === 0 && (
                      <div className={styles.emptyCell}>양식이 없습니다.</div>
                    )} */}
                  </>
                )}
              </div>
              {/* 페이지네이션 */}
              <div className={styles.paginationWrapper}>
                {displayData.length > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            </ContentCard>
          ),
        }}
      />
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
      {/* 취소 버튼 클릭 시 확인 */}
      <Confirm
        isOpen={confirmState.isOpen}
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={closeConfirm}
      />
      {/* LNB/로그아웃 클릭 시 저장 없이 이동 확인 (store의 pendingLeave) */}
      <Confirm
        isOpen={Boolean(pendingLeave)}
        message="작성 중인 내용이 있습니다. 저장하지 않고 이동하시겠습니까?"
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
      />
    </AdminLayout>
  );
}
